import { FC } from 'react';
import { useMount } from 'react-use';

import {
  Button,
  FCC,
  Heading,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useAppointmentId } from '@eluve/frontend-appointment-hooks';
import {
  LlmOutputFailureTypesEnum,
  LlmOutputFailureTypesLookup,
} from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { AppointmentStatusMessage } from './AppointmentStatusMessage';
import { RegenerateSummaryButton } from './RegenerateSummaryButton';
import { getSummarizationErrorMessage } from './summarizationErrorMessages';
import { useRetryAppointmentProcessing } from './useRetryAppointmentProcessing';

export const SummaryPendingData = () => {
  const { t } = useTranslation();
  return (
    <AppointmentStatusMessage
      title={t('appointment.summaryStates.pendingDataTitle')}
      description={t('appointment.summaryStates.pendingDataDescription')}
    />
  );
};

export const SummaryInProgress: FCC = ({ children }) => {
  const { t } = useTranslation();
  return (
    <AppointmentStatusMessage
      title={t('appointment.summaryStates.inProgressTitle')}
      description={t('appointment.summaryStates.inProgressDescription')}
    >
      {children}
    </AppointmentStatusMessage>
  );
};

export const SummaryInProgressTakingLonger: FCC = ({ children }) => {
  const { t } = useTranslation();
  return (
    <AppointmentStatusMessage
      title={t('appointment.summaryStates.takingLongerTitle')}
      description={t('appointment.summaryStates.takingLongerDescription')}
    >
      {children}
    </AppointmentStatusMessage>
  );
};

export const SummaryFailed: FC<{
  failureType?: LlmOutputFailureTypesEnum | null;
  showRegenerateButton?: boolean;
}> = ({ failureType, showRegenerateButton = true }) => {
  const { t } = useTranslation();
  const logger = useNamedLogger('AppointmentSummaryFailed');
  const errorMessage = getSummarizationErrorMessage(failureType);

  useMount(() => {
    logger.error('No summary available to display to user', {
      failureType: failureType ?? LlmOutputFailureTypesLookup.UNKNOWN,
    });
  });

  return (
    <Stack align="center" className="py-16 text-center" gap={4}>
      <AppointmentStatusMessage
        title={t(errorMessage.titleKey)}
        description={t(errorMessage.descriptionKey)}
      />
      {errorMessage.showRegenerateHint && (
        <Text className="px-12" color="tertiary" size="xs">
          {t('appointment.summaryStates.regenerateHint')}
        </Text>
      )}
      {showRegenerateButton && <RegenerateSummaryButton />}
    </Stack>
  );
};

/**
 * Shown when an appointment has been sitting in the
 * `(COMPLETED && !latestSummaryStatus)` state long enough that we suspect a
 * scheduler hole — i.e. a transcript silently failed and no LLM job will be
 * scheduled. The retry button calls the customer-facing recovery endpoint
 * which reschedules failed transcripts and reseeds summarization. Copy is
 * deliberately neutral and doesn't expose the underlying cause.
 *
 * `onBeforeRetry` lets the host app push any locally-stuck pending uploads
 * before the server-side retry runs. Optional because the extension runs file
 * sync in an offscreen document and can't reach it from the React tree.
 */
export const SummaryStuck: FC<{ onBeforeRetry?: () => Promise<unknown> }> = ({
  onBeforeRetry,
}) => {
  const { t } = useTranslation();
  const logger = useNamedLogger('AppointmentSummaryStuck');
  const toast = useToast();
  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromParams();
  const appointmentId = useAppointmentId();
  const { retryProcessing, isRetrying } = useRetryAppointmentProcessing();

  useMount(() => {
    logger.warn('SummaryStuck shown — appointment appears stuck', {
      userId,
      tenantId,
      appointmentId,
    });
  });

  const handleRetry = async () => {
    logger.info('Retry button clicked from stuck overlay', {
      userId,
      tenantId,
      appointmentId,
    });

    await onBeforeRetry?.();

    await retryProcessing(
      () => {
        toast.success(t('appointment.summaryStuck.retrySuccessTitle'), {
          description: t('appointment.summaryStuck.retrySuccessDescription'),
        });
      },
      () => {
        toast.error(t('appointment.summaryStuck.retryErrorTitle'), {
          description: t('appointment.summaryStuck.retryErrorDescription'),
        });
      },
    );
  };

  return (
    <Stack
      align="center"
      className="px-8 text-center"
      gap={6}
      justify="center"
      fullHeight
    >
      <div className="rounded-lg bg-bg-negative-subtle p-4">
        <Icon
          className="text-text-negative-subtle"
          name="AlertCircle"
          size="xl"
        />
      </div>
      <Stack align="center" gap={2}>
        <Heading size="lg" className="text-text-negative-subtle">
          {t('appointment.summarizationError.somethingWrong')}
        </Heading>
        <Text size="xs" color="tertiary" className="text-center">
          {t('appointment.summaryStuck.description')}
        </Text>
      </Stack>
      <Button
        variant="outline"
        size="sm"
        onClick={handleRetry}
        disabled={isRetrying}
        loading={isRetrying}
      >
        <Icon name="RefreshCcw" size="auto" />
        {t('appointment.summaryStuck.retryButton')}
      </Button>
    </Stack>
  );
};
